import { useLocation, useNavigate } from 'react-router';
import { useEffect, type ReactNode } from 'react';
import { useFirebaseAuth } from '@/contexts/FirebaseAuthContext';
import { useLoading } from '@/contexts/LoadingContext';
import { useHeartbeat } from '@/hooks/useHeartbeat';

interface AuthProviderProps {
  children: ReactNode;
}

const PUBLIC_PATHS = ['/login', '/asetqu-land', '/asetqu-land/assets'];

export function AuthProvider({ children }: AuthProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const isPublic = PUBLIC_PATHS.some(path => {
    if (path === '/asetqu-land/assets') return pathname === path || pathname.startsWith(path + '/');
    return pathname === path;
  });
  const { user, loading } = useFirebaseAuth();
  const { addLoadingStep, removeLoadingStep } = useLoading();
  useHeartbeat();

  // Manage loading state
  useEffect(() => {
    if (loading && !isPublic) {
      addLoadingStep('auth', 'Memverifikasi autentikasi pengguna...');

      return () => {
        removeLoadingStep('auth');
      };
    } else {
      removeLoadingStep('auth');
    }
  }, [loading, isPublic, addLoadingStep, removeLoadingStep]);

  useEffect(() => {
    // Skip auth check for public paths
    if (isPublic) {
      return;
    }

    // Wait for loading to complete
    if (loading) {
      return;
    }

    // Redirect to login if not authenticated
    if (!user) {
      navigate('/login');
      return;
    }

    // KYC check removed - KYC modal will be shown on home page instead
  }, [user, loading, isPublic, navigate]);

  // For public paths, always render children
  if (isPublic) {
    return <>{children}</>;
  }

  // If still loading, render children (loading screen is shown by LoadingContext)
  // This allows guards below to mount and add their loading steps
  if (loading) {
    return <>{children}</>;
  }

  // If not authenticated, return null (will redirect in useEffect)
  if (!user) {
    return null;
  }

  return <>{children}</>;
}
